<template>
  <b-modal v-model="visible" size="lg" hide-footer hide-header no-close-on-backdrop
           body-class="co-modal-body" content-class="co-modal-content" @hidden="onHidden">

    <!-- Close button -->
    <button class="co-close" @click="visible = false" :disabled="paying">
      <feather-icon icon="XIcon" size="18"/>
    </button>

    <!-- ── Step 1: Review ── -->
    <div v-if="step === 'review'" class="co-step">
      <!-- Step indicator -->
      <div class="co-steps mb-2">
        <div class="co-steps__item co-steps__item--active">
          <span class="co-steps__num">1</span>
          <span class="co-steps__label">{{ msg('Review') }}</span>
        </div>
        <div class="co-steps__line"/>
        <div class="co-steps__item">
          <span class="co-steps__num">2</span>
          <span class="co-steps__label">{{ msg('Payment') }}</span>
        </div>
        <div class="co-steps__line"/>
        <div class="co-steps__item">
          <span class="co-steps__num">3</span>
          <span class="co-steps__label">{{ msg('Done') }}</span>
        </div>
      </div>

      <h5 class="co-heading mb-1">{{ msg('Order Summary') }}</h5>

      <!-- Items list -->
      <div class="co-items">
        <div v-for="item in cart" :key="item.id" class="co-item">
          <div class="co-item__thumb">
            <img v-if="item.imageUrls && item.imageUrls.length" :src="getUrl(item.imageUrls[0])"/>
            <div v-else class="co-item__thumb-placeholder">
              <feather-icon icon="PackageIcon" size="18"/>
            </div>
          </div>
          <div class="co-item__info">
            <span class="co-item__name">{{ item.name }}</span>
            <span class="co-item__qty">{{ item.quantity }} &times; {{ formatPrice(item.price) }}</span>
          </div>
          <span class="co-item__total">{{ formatPrice(item.price * item.quantity) }}</span>
        </div>
      </div>

      <!-- Total -->
      <div class="co-total">
        <span>{{ msg('Total') }}</span>
        <span class="co-total__amount">{{ formatPrice(total) }}</span>
      </div>

      <!-- Actions -->
      <div class="co-actions">
        <button class="co-btn co-btn--outline" @click="visible = false">{{ msg('Cancel') }}</button>
        <button class="co-btn co-btn--primary" :disabled="loading" @click="initPayment">
          <b-spinner v-if="loading" small class="mr-50"/>
          <feather-icon v-else icon="ArrowRightIcon" size="16" class="mr-50"/>
          {{ msg('Continue to Payment') }}
        </button>
      </div>
    </div>

    <!-- ── Step 2: Payment ── -->
    <div v-if="step === 'payment'" class="co-step">
      <!-- Step indicator -->
      <div class="co-steps mb-2">
        <div class="co-steps__item co-steps__item--done">
          <feather-icon icon="CheckIcon" size="14"/>
          <span class="co-steps__label">{{ msg('Review') }}</span>
        </div>
        <div class="co-steps__line co-steps__line--done"/>
        <div class="co-steps__item co-steps__item--active">
          <span class="co-steps__num">2</span>
          <span class="co-steps__label">{{ msg('Payment') }}</span>
        </div>
        <div class="co-steps__line"/>
        <div class="co-steps__item">
          <span class="co-steps__num">3</span>
          <span class="co-steps__label">{{ msg('Done') }}</span>
        </div>
      </div>

      <!-- Secure header -->
      <div class="co-secure-header">
        <div class="co-secure-header__icon">
          <feather-icon icon="ShieldIcon" size="22"/>
        </div>
        <div>
          <h5 class="mb-0">{{ msg('Secure Payment') }}</h5>
          <small class="co-secure-header__sub">{{ msg('Your payment information is encrypted and secure') }}</small>
        </div>
      </div>

      <!-- Amount display -->
      <div class="co-amount-display">
        <span class="co-amount-display__label">{{ msg('Amount to pay') }}</span>
        <span class="co-amount-display__value">{{ formatPrice(total) }}</span>
      </div>

      <!-- Card input -->
      <div class="co-card-section">
        <label class="co-card-label">
          <feather-icon icon="CreditCardIcon" size="14" class="mr-50"/>
          {{ msg('Card Details') }}
        </label>
        <div ref="cardElement" class="co-card-input"></div>
      </div>

      <!-- Error -->
      <div v-if="cardError" class="co-error">
        <feather-icon icon="AlertCircleIcon" size="14" class="mr-50"/>
        {{ cardError }}
      </div>

      <!-- Trust badges -->
      <div class="co-trust">
        <div class="co-trust__item">
          <feather-icon icon="LockIcon" size="13"/>
          <span>{{ msg('SSL Encrypted') }}</span>
        </div>
        <div class="co-trust__item">
          <feather-icon icon="ShieldIcon" size="13"/>
          <span>{{ msg('PCI Compliant') }}</span>
        </div>
        <div class="co-trust__item">
          <svg class="co-trust__stripe-icon" viewBox="0 0 60 25" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 10.2c0-.7.6-1 1.5-1 1.4 0 3 .4 4.4 1.2V6.3c-1.5-.6-3-.8-4.4-.8C3.2 5.5.8 7.4.8 10.4c0 4.7 6.5 4 6.5 6 0 .8-.7 1.1-1.7 1.1-1.5 0-3.4-.6-4.9-1.4v4.2c1.7.7 3.3 1 4.9 1 3.4 0 5.8-1.7 5.8-4.7C11.4 12 5 12.8 5 10.2z"/>
            <path d="M15.9 3l-4 .9v3.5h4V3zM11.9 8.7v11.6h4V8.7h-4zM21 8.7l-.3 1.2h-3.5v-3c0-1.2.3-1.6 1-1.6h.6V1.2l-.8-.1h-.1c-2.4 0-4.5 1.4-4.5 4.7v3h-1.5V12h1.5v8.3h3.8V12h2.5l.6-3.3H17.7zM28 5.5c-1.5 0-2.5.7-3 1.2L24.7 5.5h-3.5V24l4-.8v-4c.5.4 1.3.9 2.6.9 2.7 0 5.1-2.1 5.1-6.9C33 9.4 30.6 5.5 28 5.5zm-.9 10.4c-.9 0-1.4-.3-1.7-.7V10c.4-.5.9-.8 1.7-.8 1.3 0 2.2 1.5 2.2 3.1 0 1.8-.9 3.3-2.2 3.6zM38.3 5.5c-3.4 0-5.5 2.8-5.5 7s2.2 6.8 5.7 6.8c1.6 0 2.9-.4 3.8-.9v-3c-.9.5-2 .8-3.2.8-1.3 0-2.4-.5-2.5-2h6.3c0-.2 0-.9 0-1.3-.1-4.2-1.8-7.4-4.6-7.4zm-1.7 5.6c0-1.5.9-2.1 1.7-2.1.8 0 1.6.6 1.6 2.1h-3.3z"/>
          </svg>
          <span>{{ msg('Powered by Stripe') }}</span>
        </div>
      </div>

      <!-- Actions -->
      <div class="co-actions">
        <button class="co-btn co-btn--outline" :disabled="paying" @click="step = 'review'">
          <feather-icon icon="ArrowLeftIcon" size="14" class="mr-25"/>
          {{ msg('Back') }}
        </button>
        <button class="co-btn co-btn--pay" :disabled="paying" @click="confirmPayment">
          <b-spinner v-if="paying" small class="mr-50"/>
          <feather-icon v-else icon="LockIcon" size="16" class="mr-50"/>
          {{ paying ? msg('Processing...') : msg('Pay') + ' ' + formatPrice(total) }}
        </button>
      </div>
    </div>

    <!-- ── Step 3: Success ── -->
    <div v-if="step === 'success'" class="co-step co-step--success">
      <!-- Step indicator -->
      <div class="co-steps mb-2">
        <div class="co-steps__item co-steps__item--done">
          <feather-icon icon="CheckIcon" size="14"/>
          <span class="co-steps__label">{{ msg('Review') }}</span>
        </div>
        <div class="co-steps__line co-steps__line--done"/>
        <div class="co-steps__item co-steps__item--done">
          <feather-icon icon="CheckIcon" size="14"/>
          <span class="co-steps__label">{{ msg('Payment') }}</span>
        </div>
        <div class="co-steps__line co-steps__line--done"/>
        <div class="co-steps__item co-steps__item--active co-steps__item--success">
          <feather-icon icon="CheckIcon" size="14"/>
          <span class="co-steps__label">{{ msg('Done') }}</span>
        </div>
      </div>

      <div class="co-success">
        <div class="co-success__circle">
          <div class="co-success__circle-inner">
            <feather-icon icon="CheckIcon" size="36"/>
          </div>
        </div>
        <h4 class="co-success__title">{{ msg('Payment Successful') }}</h4>
        <p class="co-success__sub">
          {{ msg('Your order has been confirmed and an invoice has been created.') }}
        </p>
        <div class="co-success__amount">{{ formatPrice(total) }}</div>
        <button class="co-btn co-btn--primary mt-2" @click="visible = false">
          {{ msg('Done') }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import { loadStripe } from '@stripe/stripe-js'

export default {
  name: 'CatalogCheckoutModal',
  props: {
    value: Boolean,
    cart: { type: Array, default: () => [] },
    buildingId: { type: String, default: null },
    currency: { type: String, default: 'EUR' },
  },
  data() {
    return {
      step: 'review',
      loading: false,
      paying: false,
      cardError: null,
      orderId: null,
      clientSecret: null,
      stripe: null,
      elements: null,
      cardEl: null,
    }
  },
  computed: {
    visible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },
    total() {
      return this.cart.reduce((s, i) => s + i.price * i.quantity, 0)
    },
  },
  methods: {
    ...mapActions('catalog', ['createOrder', 'confirmOrder']),
    formatPrice(v) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: this.currency }).format(v)
    },
    async initPayment() {
      this.loading = true
      this.cardError = null
      try {
        const items = this.cart.map(i => ({ itemId: i.id, quantity: i.quantity }))
        const result = await this.createOrder({ buildingId: this.buildingId, items })
        this.orderId = result.order.id
        this.clientSecret = result.clientSecret

        this.stripe = await loadStripe(result.publishableKey)
        this.elements = this.stripe.elements()
        this.step = 'payment'
        await this.mountCardElement()
      } catch (e) {
        this.cardError = typeof e === 'string' ? e : (e.message || 'Payment initialization failed')
      } finally {
        this.loading = false
      }
    },
    mountCardElement() {
      return new Promise((resolve) => {
        const tryMount = (attempts) => {
          this.$nextTick(() => {
            if (this.$refs.cardElement) {
              const isDark = document.body.classList.contains('dark-layout')
              this.cardEl = this.elements.create('card', {
                style: {
                  base: {
                    fontSize: '16px',
                    color: isDark ? '#d0d2d6' : '#5e5873',
                    fontFamily: '"Montserrat", Helvetica, Arial, serif',
                    iconColor: isDark ? '#676d7d' : '#b9b9c3',
                    '::placeholder': { color: isDark ? '#676d7d' : '#b9b9c3' },
                  },
                  invalid: { color: '#ea5455', iconColor: '#ea5455' },
                },
              })
              this.cardEl.mount(this.$refs.cardElement)
              this.cardEl.on('change', e => { this.cardError = e.error ? e.error.message : null })
              resolve()
            } else if (attempts > 0) {
              setTimeout(() => tryMount(attempts - 1), 50)
            } else {
              resolve()
            }
          })
        }
        tryMount(20)
      })
    },
    async confirmPayment() {
      this.paying = true
      this.cardError = null
      try {
        const { error, paymentIntent } = await this.stripe.confirmCardPayment(
          this.clientSecret,
          { payment_method: { card: this.cardEl } }
        )
        if (error) {
          this.cardError = error.message
          return
        }
        if (paymentIntent.status === 'succeeded') {
          await this.confirmOrder({ orderId: this.orderId, paymentIntentId: paymentIntent.id })
          this.step = 'success'
          this.$emit('paid')
        }
      } catch (e) {
        this.cardError = typeof e === 'string' ? e : (e.message || 'Payment failed')
      } finally {
        this.paying = false
      }
    },
    onHidden() {
      this.step = 'review'
      this.loading = false
      this.paying = false
      this.cardError = null
      this.orderId = null
      this.clientSecret = null
      if (this.cardEl) { this.cardEl.destroy(); this.cardEl = null }
      this.stripe = null
      this.elements = null
    },
  },
}
</script>

<style scoped>
/* ── Modal shell ── */
.co-step { padding: .5rem .25rem; }
.co-close {
  position: absolute; top: 14px; right: 16px; z-index: 10; border: none; background: none;
  color: #b4b7bd; cursor: pointer; padding: 4px; border-radius: 6px; transition: all .15s;
}
.co-close:hover { background: rgba(0,0,0,.05); color: #5e5873; }

/* ── Step indicator ── */
.co-steps {
  display: flex; align-items: center; justify-content: center; gap: 0; padding: 0 1rem;
}
.co-steps__item {
  display: flex; flex-direction: column; align-items: center; gap: 4px; min-width: 60px;
}
.co-steps__num {
  width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: .78rem; font-weight: 700;
  background: #eeedfd; color: #b4b7bd; transition: all .25s;
}
.co-steps__label { font-size: .72rem; color: #b4b7bd; font-weight: 600; transition: color .25s; }
.co-steps__line {
  flex: 1; height: 2px; background: #eeedfd; margin: 0 6px; margin-bottom: 18px; border-radius: 2px;
  transition: background .25s;
}
.co-steps__line--done { background: #7367f0; }
.co-steps__item--active .co-steps__num {
  background: #7367f0; color: #fff; box-shadow: 0 2px 8px rgba(115,103,240,.3);
}
.co-steps__item--active .co-steps__label { color: #7367f0; }
.co-steps__item--done .co-steps__num,
.co-steps__item--done svg {
  background: #7367f0; color: #fff;
}
.co-steps__item--done { color: #fff; }
.co-steps__item--done > svg {
  width: 28px; height: 28px; border-radius: 50%; padding: 6px;
  background: #7367f0; color: #fff;
}
.co-steps__item--done .co-steps__label { color: #7367f0; }
.co-steps__item--success .co-steps__num,
.co-steps__item--success > svg {
  background: #28c76f !important; box-shadow: 0 2px 8px rgba(40,199,111,.3);
}
.co-steps__item--success .co-steps__label { color: #28c76f !important; }

/* ── Heading ── */
.co-heading { font-size: 1.1rem; font-weight: 700; color: #5e5873; }

/* ── Items list ── */
.co-items { margin-bottom: 1rem; }
.co-item {
  display: flex; align-items: center; padding: .65rem 0;
  border-bottom: 1px solid #f0f2f5;
}
.co-item:last-child { border-bottom: none; }
.co-item__thumb {
  width: 44px; height: 44px; border-radius: 8px; overflow: hidden; flex-shrink: 0; margin-right: .75rem;
}
.co-item__thumb img { width: 100%; height: 100%; object-fit: cover; }
.co-item__thumb-placeholder {
  width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
  background: #f5f6fa; color: #b4b7bd;
}
.co-item__info { flex: 1; display: flex; flex-direction: column; }
.co-item__name { font-weight: 600; font-size: .9rem; color: #5e5873; }
.co-item__qty { font-size: .78rem; color: #a8a8b8; }
.co-item__total { font-weight: 700; font-size: .95rem; color: #5e5873; }

/* ── Total ── */
.co-total {
  display: flex; justify-content: space-between; align-items: center;
  padding: .75rem 0; border-top: 2px solid #eeedfd;
  font-weight: 700; font-size: 1rem; color: #5e5873;
}
.co-total__amount { font-size: 1.25rem; color: #7367f0; }

/* ── Secure header ── */
.co-secure-header {
  display: flex; align-items: center; gap: 12px;
  background: linear-gradient(135deg, rgba(115,103,240,.06), rgba(115,103,240,.02));
  border: 1px solid rgba(115,103,240,.12); border-radius: 12px;
  padding: .85rem 1.15rem; margin-bottom: 1.25rem;
}
.co-secure-header__icon {
  width: 42px; height: 42px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg, #7367f0, #9e95f5); color: #fff;
  display: flex; align-items: center; justify-content: center;
}
.co-secure-header h5 { font-size: 1rem; font-weight: 700; color: #5e5873; }
.co-secure-header__sub { color: #a8a8b8; font-size: .78rem; }

/* ── Amount display ── */
.co-amount-display {
  display: flex; justify-content: space-between; align-items: center;
  background: #f8f8fb; border-radius: 10px; padding: .75rem 1.15rem; margin-bottom: 1.25rem;
}
.co-amount-display__label { font-size: .85rem; color: #a8a8b8; font-weight: 500; }
.co-amount-display__value { font-size: 1.3rem; font-weight: 800; color: #7367f0; letter-spacing: -.02em; }

/* ── Card input ── */
.co-card-section { margin-bottom: 1rem; }
.co-card-label {
  display: flex; align-items: center; font-size: .82rem; font-weight: 600;
  color: #6e6b7b; margin-bottom: .5rem;
}
.co-card-input {
  border: 1.5px solid #d8d6de; border-radius: 10px; padding: .85rem 1rem;
  transition: border-color .2s, box-shadow .2s;
  background: #fff;
}
.co-card-input:focus-within {
  border-color: #7367f0;
  box-shadow: 0 0 0 3px rgba(115,103,240,.12);
}

/* ── Error ── */
.co-error {
  display: flex; align-items: center; gap: 4px;
  background: rgba(234,84,85,.08); border: 1px solid rgba(234,84,85,.2);
  color: #ea5455; border-radius: 8px; padding: .5rem .85rem;
  font-size: .82rem; font-weight: 500; margin-bottom: 1rem;
}

/* ── Trust badges ── */
.co-trust {
  display: flex; flex-wrap: wrap; gap: 12px; justify-content: center;
  margin-bottom: 1.25rem; padding: .5rem 0;
}
.co-trust__item {
  display: flex; align-items: center; gap: 5px;
  font-size: .72rem; color: #a8a8b8; font-weight: 500;
}
.co-trust__stripe-icon { width: 32px; height: 14px; }

/* ── Actions ── */
.co-actions { display: flex; justify-content: flex-end; gap: 10px; padding-top: .5rem; }

/* ── Buttons ── */
.co-btn {
  display: inline-flex; align-items: center; justify-content: center;
  padding: .6rem 1.25rem; border-radius: 10px; font-size: .9rem; font-weight: 600;
  cursor: pointer; transition: all .2s; border: none;
}
.co-btn:disabled { opacity: .55; cursor: not-allowed; }
.co-btn--outline {
  background: transparent; border: 1.5px solid #d8d6de; color: #6e6b7b;
}
.co-btn--outline:hover:not(:disabled) { background: #f8f8fb; border-color: #b4b7bd; }
.co-btn--primary {
  background: linear-gradient(135deg, #7367f0, #9e95f5); color: #fff;
}
.co-btn--primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #6358e0, #8c84e8);
  box-shadow: 0 4px 14px rgba(115,103,240,.3);
}
.co-btn--pay {
  background: linear-gradient(135deg, #28c76f, #48da89); color: #fff;
  padding: .65rem 1.5rem; font-size: .95rem;
}
.co-btn--pay:hover:not(:disabled) {
  background: linear-gradient(135deg, #1fad5f, #3bc77a);
  box-shadow: 0 4px 14px rgba(40,199,111,.35);
  transform: translateY(-1px);
}

/* ── Success ── */
.co-success { text-align: center; padding: 1rem 0; }
.co-success__circle {
  width: 88px; height: 88px; border-radius: 50%; margin: 0 auto 1.25rem;
  background: rgba(40,199,111,.1); display: flex; align-items: center; justify-content: center;
  animation: successPulse 2s ease-in-out infinite;
}
.co-success__circle-inner {
  width: 64px; height: 64px; border-radius: 50%;
  background: linear-gradient(135deg, #28c76f, #48da89); color: #fff;
  display: flex; align-items: center; justify-content: center;
  animation: popIn .5s cubic-bezier(.26,.53,.74,1.48);
}
.co-success__title { font-size: 1.25rem; font-weight: 700; color: #28c76f; margin-bottom: .25rem; }
.co-success__sub { color: #a8a8b8; font-size: .88rem; margin-bottom: .5rem; }
.co-success__amount {
  display: inline-block; background: rgba(40,199,111,.08); color: #28c76f;
  font-size: 1.3rem; font-weight: 800; padding: .35rem 1.25rem; border-radius: 10px;
}

@keyframes popIn {
  0% { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes successPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(40,199,111,.15); }
  50% { box-shadow: 0 0 0 12px rgba(40,199,111,0); }
}
</style>

<style>
/* ── Modal overrides ── */
.co-modal-content { border-radius: 16px !important; overflow: hidden; }
.co-modal-body { padding: 1.5rem 1.75rem !important; }

/* ── Dark mode ── */
.dark-layout .co-modal-content { background-color: #283046 !important; }
.dark-layout .co-close { color: #676d7d; }
.dark-layout .co-close:hover { background: rgba(255,255,255,.06); color: #d0d2d6; }

.dark-layout .co-steps__num { background: rgba(115,103,240,.15); color: #676d7d; }
.dark-layout .co-steps__label { color: #676d7d; }
.dark-layout .co-steps__line { background: rgba(255,255,255,.08); }
.dark-layout .co-steps__item--active .co-steps__num { background: #7367f0; color: #fff; }
.dark-layout .co-steps__item--active .co-steps__label { color: #7367f0; }
.dark-layout .co-steps__item--done > svg { background: #7367f0; color: #fff; }
.dark-layout .co-steps__item--done .co-steps__label { color: #7367f0; }
.dark-layout .co-steps__line--done { background: #7367f0; }

.dark-layout .co-heading { color: #d0d2d6; }
.dark-layout .co-item { border-bottom-color: rgba(255,255,255,.06); }
.dark-layout .co-item__name { color: #d0d2d6; }
.dark-layout .co-item__qty { color: #676d7d; }
.dark-layout .co-item__total { color: #d0d2d6; }
.dark-layout .co-item__thumb-placeholder { background: rgba(255,255,255,.05); color: #4b5572; }
.dark-layout .co-total { border-top-color: rgba(255,255,255,.08); color: #d0d2d6; }

.dark-layout .co-secure-header {
  background: linear-gradient(135deg, rgba(115,103,240,.1), rgba(115,103,240,.04));
  border-color: rgba(115,103,240,.2);
}
.dark-layout .co-secure-header h5 { color: #d0d2d6; }
.dark-layout .co-secure-header__sub { color: #676d7d; }

.dark-layout .co-amount-display { background: rgba(255,255,255,.04); }
.dark-layout .co-amount-display__label { color: #676d7d; }

.dark-layout .co-card-label { color: #b4b7bd; }
.dark-layout .co-card-input {
  background: rgba(255,255,255,.03); border-color: rgba(255,255,255,.12);
}
.dark-layout .co-card-input:focus-within {
  border-color: #7367f0; box-shadow: 0 0 0 3px rgba(115,103,240,.15);
}

.dark-layout .co-error {
  background: rgba(234,84,85,.1); border-color: rgba(234,84,85,.25);
}

.dark-layout .co-trust__item { color: #676d7d; }

.dark-layout .co-btn--outline { border-color: rgba(255,255,255,.12); color: #b4b7bd; }
.dark-layout .co-btn--outline:hover:not(:disabled) {
  background: rgba(255,255,255,.05); border-color: rgba(255,255,255,.2);
}

.dark-layout .co-success__title { color: #28c76f; }
.dark-layout .co-success__sub { color: #676d7d; }
.dark-layout .co-success__amount { background: rgba(40,199,111,.1); }
.dark-layout .co-success__circle { background: rgba(40,199,111,.08); }
</style>
